import {
  Button,
  collapseColumn,
  column,
  Css,
  emptyCell,
  GridColumn,
  GridDataRow,
  GridTable,
  ModalBody,
  ModalHeader,
  simpleHeader,
  Tag,
  Tooltip,
} from "@homebound/beam";
import { Price } from "src/components";
import {
  CheckoutPricingScopeLineItemDetail_TakeoffLineItemVersionFragment,
  CheckoutPricingTraceGroupDetailQuery,
  useCheckoutPricingTraceGroupDetailQuery,
} from "./CheckoutPricingTracePage.generated";

type CheckoutPricingTraceModalProps = { pofId: string; rpavId: string; oldRpoIds: string[]; newRpoIds: string[] };

/** Shows the scope change from one config to another, to debug "why did my price change?" */
export function CheckoutPricingTraceModal(props: CheckoutPricingTraceModalProps) {
  const { pofId, rpavId, oldRpoIds, newRpoIds } = props;
  const { data } = useCheckoutPricingTraceGroupDetailQuery({ variables: { pofId, rpavId, oldRpoIds, newRpoIds } });
  const columns = createColumns();
  const rows = !data ? [] : createRows(data);
  return (
    <>
      <ModalHeader>Group Details</ModalHeader>
      <ModalBody>
        <GridTable rows={rows} columns={columns} />
      </ModalBody>
    </>
  );
}

type HeaderRow = { kind: "header" };
type TotalRow = { kind: "total"; data: { totalCostInCents: number } };
type CostCodeRow = { kind: "costCode"; id: string; data: { costCodeName: string; costDiffInCents: number } };
type TliRow = {
  kind: "tli";
  id: string;
  type: "added" | "removed";
  data: CheckoutPricingScopeLineItemDetail_TakeoffLineItemVersionFragment;
};
type Row = HeaderRow | TotalRow | CostCodeRow | TliRow;

function createColumns(): GridColumn<Row>[] {
  return [
    collapseColumn<Row>(),
    column<Row>({
      header: "Name",
      total: "Total",
      costCode: (d) => d.costCodeName,
      tli: (tli) => (
        <div css={Css.df.fdc.gap1.$}>
          <Tooltip title={tli.id}>
            {tli.name} ({tli.location.displayLocationPath})
          </Tooltip>
          <div css={Css.df.gap1.$}>
            {tli.options
              .sortBy((o) => o.optionGroup.order)
              .map((o) => (
                <Tag key={o.id} text={`${o.code} ${o.name}`} type="info" />
              ))}
          </div>
        </div>
      ),
      w: "500px",
    }),
    column<Row>({
      header: "Quantity",
      total: emptyCell,
      costCode: emptyCell,
      tli: (tli) => ({ content: tli.quantity }),
      align: "right",
    }),
    column<Row>({
      header: "Cost",
      total: (d) => <Price displayDirection valueInCents={d.totalCostInCents} />,
      costCode: (d) => <Price displayDirection={true} valueInCents={d.costDiffInCents} />,
      tli: (tli, { row }) => {
        const bcli = tli.preferredBidContractLineItem;
        if (!bcli) {
          return "n/a";
        }
        return {
          content: (
            <Price
              displayDirection={true}
              valueInCents={tli.totalCostInCents * (row.type === "added" ? 1 : -1)}
              zeroIs="neutral"
            />
          ),
        };
      },
      align: "right",
    }),
    column<Row>({
      header: "BCR",
      total: "",
      costCode: emptyCell,
      tli: (tli) => {
        const bcli = tli.preferredBidContractLineItem;
        return (
          bcli && (
            <Button
              openInNew={true}
              onClick={bcli.revision.blueprintUrl.path}
              label={bcli.revision.id}
              variant="text"
              icon={null}
            />
          )
        );
      },
      align: "right",
    }),
  ];
}

function createRows(data: CheckoutPricingTraceGroupDetailQuery): GridDataRow<Row>[] {
  const costCodeRows = groupDetailsByCostCode(data);
  return [
    simpleHeader,
    { id: "total", kind: "total" as const, data: { totalCostInCents: costCodeRows.sum((cc) => cc.diffInCents) } },
    ...costCodeRows
      .sortBy((cc) => Math.abs(cc.diffInCents) * -1)
      .map((cc) => {
        const added = cc.added.map((tli) => ({ id: tli.id, kind: "tli", type: "added", data: tli }) as const);
        const removed = cc.removed.map((tli) => ({ id: tli.id, kind: "tli", type: "removed", data: tli }) as const);
        return {
          kind: "costCode" as const,
          id: cc.costCode.id,
          data: { costCodeName: cc.costCode.displayName, costDiffInCents: cc.diffInCents },
          // If there are only 1 or 2 cost codes, just expand
          initCollapsed: costCodeRows.length > 2,
          children: [...added, ...removed].sortBy((row) => [
            row.data.location.displayLocationPath,
            Math.abs(row.data.totalCostInCents) * -1,
          ]),
        };
      }),
  ];
}

type DetailsByCostCode = {
  costCode: { id: string; displayName: string };
  diffInCents: number;
  added: CheckoutPricingScopeLineItemDetail_TakeoffLineItemVersionFragment[];
  removed: CheckoutPricingScopeLineItemDetail_TakeoffLineItemVersionFragment[];
};

function groupDetailsByCostCode(query: CheckoutPricingTraceGroupDetailQuery): DetailsByCostCode[] {
  const { added, removed } = query.calculateScopeChange;
  // Group the "added" and "removed" lists by the same cost code ids
  const addedByCostCode = added?.groupBy((tli) => tli.item.costCode.id) ?? {};
  const removedByCostCode = removed?.groupBy((tli) => tli.item.costCode.id) ?? {};
  // Then combine the two lists by cost code
  return [...added, ...removed]
    .map((tli) => tli.item.costCode)
    .unique()
    .map((costCode) => {
      const added = addedByCostCode[costCode.id] ?? [];
      const removed = removedByCostCode[costCode.id] ?? [];
      const diffInCents = added.sum((tliv) => tliv.totalCostInCents) - removed.sum((tliv) => tliv.totalCostInCents);
      return { costCode, diffInCents, added, removed };
    });
}
